import encodeQR from '@paulmillr/qr';

import { pathFor } from './qrPath';
import styles from './QrCode.module.css';

/**
 * The enrolment URI as a scannable square, rendered ENTIRELY IN THE BROWSER.
 *
 * ## Why not a chart service
 *
 * The obvious `<img src="https://api.qrserver.com/...?data={otpauth_uri}">`
 * sends the TOTP secret to a third party in a query string, where it lands in
 * their access log and in every proxy between here and there. It would also be
 * blocked: this application ships `default-src 'self'`, so the request never
 * leaves. The encoder is bundled, has no dependencies, and makes no request at
 * all.
 *
 * ITS COST, MEASURED RATHER THAN ASSUMED, on the branch that added it:
 * 305.36 kB -> 322.13 kB raw, 95.20 -> 101.71 kB gzipped. So **16.8 kB raw,
 * 6.5 kB gzipped** for `@paulmillr/qr`, taken by stubbing the import and
 * rebuilding. An earlier draft of this comment claimed 4 kB from the package's
 * README; it is four times that, and the number is here so the next person
 * weighing it is weighing the real one.
 *
 * ## Why a single `<path>` and not one `<rect>` per module
 *
 * The library's own `'svg'` output emits a rect per dark module — 52 kB of
 * markup for a version-7 code. The raw matrix costs one path of about 4 kB, and
 * it is a real DOM node rather than `dangerouslySetInnerHTML`, which is the
 * other reason: this string is derived from a server response, and there is no
 * version of "inject untrusted markup" worth reaching for here.
 *
 * ## Why it is not themed
 *
 * `black` on `white`, in both themes, deliberately. A QR code is a
 * machine-readable image and not a UI surface: ISO/IEC 18004 specifies dark
 * modules on a light background, and while many decoders cope with an inverted
 * code, "many" is not a property to bet an enrolment on — a user whose phone
 * will not scan has no way to tell that the theme is why. The quiet zone the
 * encoder includes is part of the same contract; the white plate is what makes
 * it a quiet zone rather than a border drawn on whatever is behind it.
 */

export interface QrCodeProps {
  /** The `otpauth://` URI. Not rendered as text anywhere. */
  value: string;
  /** The accessible name. There is no useful alternative text for a QR code
   *  beyond saying what it is — the secret is offered separately, in a field a
   *  screen-reader user can actually read and copy. */
  label: string;
}

export function QrCode({ value, label }: QrCodeProps) {
  const matrix = encodeQR(value, 'raw', { ecc: 'medium' });
  const size = matrix.length;

  return (
    <svg
      className={styles['qr']}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={label}
      data-testid="enrolmentQr"
      // Modules are whole pixels at any scale, so the code stays crisp instead
      // of being blurred into an unreadable grey by the browser's smoothing.
      shapeRendering="crispEdges"
    >
      <rect width={size} height={size} fill="white" />
      <path d={pathFor(matrix)} fill="black" />
    </svg>
  );
}
